import React, { useState, useEffect, useMemo } from "react";
import { PieChart, BarChart } from "../../../common/ReusableComponent/Chart";
import {
  Users,
  Clock,
  TrendingUp,
  Activity,
  AlertTriangle,
  CheckCircle2,
  CalendarDays,
  TimerReset,
  BarChart3,
  PieChart as PieChartIcon,
  UserX,
} from "lucide-react";
import useApi from "../../../../utils/api-manager/Helper/useApi";

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCount = (value) => safeNumber(value).toLocaleString("en-IN");

const formatPercent = (value) => {
  const parsed = safeNumber(value);
  return `${parsed.toFixed(parsed % 1 === 0 ? 0 : 1)}%`;
};

const getHealthTone = (underHoursPercentage) => {
  const percentage = safeNumber(underHoursPercentage);

  if (percentage >= 30) return "red";
  if (percentage >= 15) return "amber";
  return "green";
};

const KpiCard = ({ icon: Icon, label, value, helper, tone = "blue" }) => (
  <div className={`tcdb-kpi-card tcdb-tone-${tone}`}>
    <div className="tcdb-kpi-icon">
      <Icon size={21} />
    </div>
    <div className="tcdb-kpi-content">
      <div className="tcdb-kpi-label">{label}</div>
      <div className="tcdb-kpi-value">{value}</div>
      {helper && <div className="tcdb-kpi-helper">{helper}</div>}
    </div>
  </div>
);

const InsightCard = ({ icon: Icon, title, value, note, tone = "blue", progress }) => (
  <div className={`tcdb-insight-card tcdb-tone-${tone}`}>
    <div className="tcdb-insight-top">
      <div className="tcdb-insight-icon">
        <Icon size={19} />
      </div>
      <div>
        <h3>{title}</h3>
        <p>{note}</p>
      </div>
    </div>
    <div className="tcdb-insight-value">{value}</div>
    {typeof progress === "number" && (
      <div className="tcdb-progress">
        <span style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }} />
      </div>
    )}
  </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="tcdb-section-header">
    <div className="tcdb-section-icon">
      <Icon size={18} />
    </div>
    <div>
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="tcdb-empty-state">
    <CheckCircle2 size={28} />
    <span>{message}</span>
  </div>
);

export default function NewDashboard() {
  const { Post } = useApi();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchTimeCardData();
  }, []);

  const fetchTimeCardData = async () => {
    try {
      setLoading(true);
      const currentYear = new Date().getFullYear();

      const response = await Post("timeCardDashboardReport", {
        start_date: `${currentYear}-01-01`,
        end_date: `${currentYear}-12-31`,
      });

      setData(response?.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const totalEmployees = safeNumber(data?.total_employees);
  const underHoursCount = safeNumber(data?.under_hours_count);
  const underHoursPercentage = safeNumber(data?.under_hours_percentage);
  const overHoursCount = safeNumber(data?.over_hours_count);
  const overHoursPercentage = safeNumber(data?.over_hours_percentage);
  const earlyArrivalsCount = safeNumber(data?.early_arrivals_count);
  const earlyArrivalsPercentage = safeNumber(data?.early_arrivals_percentage);

  const logTimePercentage = data?.log_time_percentage || {};
  const weeklyEmployeeStatus = data?.weekly_employee_status || {};
  const underHoursEmployees = data?.list_of_employees_that_has_worked_less_than_required || [];

  const onTimePercentage = safeNumber(logTimePercentage.on_time);
  const latePercentage = safeNumber(logTimePercentage.late);
  const earlyPercentage = safeNumber(logTimePercentage.early);
  const attendanceHealthTone = getHealthTone(underHoursPercentage);
  const attendanceHealthValue = Math.max(0, 100 - underHoursPercentage);

  const currentDateLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const pieData = useMemo(
    () => ({
      labels: ["Early", "On Time", "Late"],
      datasets: [
        {
          data: [earlyPercentage, onTimePercentage, latePercentage],
          backgroundColor: ["#059669", "#2563eb", "#dc2626"],
        },
      ],
    }),
    [earlyPercentage, onTimePercentage, latePercentage]
  );

  const weeklyData = useMemo(
    () => ({
      labels: Object.keys(weeklyEmployeeStatus),
      datasets: [
        {
          label: "Early / On Time",
          data: Object.values(weeklyEmployeeStatus).map((day) => safeNumber(day.early_or_on_time)),
          backgroundColor: "#059669",
        },
        {
          label: "Late",
          data: Object.values(weeklyEmployeeStatus).map((day) => safeNumber(day.late)),
          backgroundColor: "#dc2626",
        },
      ],
    }),
    [weeklyEmployeeStatus]
  );

  const visibleUnderHoursEmployees = underHoursEmployees.slice(0, 10);
  const remainingUnderHoursCount = Math.max(underHoursEmployees.length - visibleUnderHoursEmployees.length, 0);

  if (loading) {
    return (
      <div className="responsive-box tcdb-page">
        <style>{dashboardStyles}</style>
        <div className="tcdb-loading">
          <div className="tcdb-spinner" />
          <div>
            <h3>Loading Timecard Dashboard</h3>
            <p>Preparing attendance and work-hour insights.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="responsive-box tcdb-page">
        <style>{dashboardStyles}</style>
        <div className="tcdb-empty-page">
          <div className="tcdb-empty-page-icon">
            <Users size={34} />
          </div>
          <h2>No Timecard Data Found</h2>
          <p>Add employee attendance or timecard records to start viewing workforce insights.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="responsive-box tcdb-page">
      <style>{dashboardStyles}</style>

      <div className="tcdb-shell">
        <section className="tcdb-hero">
          <div>
            <div className="tcdb-kicker">
              <span className="tcdb-kicker-dot" />
              Workforce Business View
            </div>
            <h1>Timecard Dashboard</h1>
            <p>
              Simple executive view for employee attendance, working hours, punctuality and exception follow-up.
            </p>
            <div className="tcdb-date-pill">
              <CalendarDays size={14} />
              {currentDateLabel}
            </div>
          </div>

          <div className="tcdb-hero-kpis">
            <KpiCard
              icon={Users}
              label="Total Employees"
              value={formatCount(totalEmployees)}
              helper="Employees considered in this report"
              tone="blue"
            />
            <KpiCard
              icon={AlertTriangle}
              label="Under Hours"
              value={`${formatCount(underHoursCount)} (${formatPercent(underHoursPercentage)})`}
              helper="Requires attention"
              tone={underHoursCount > 0 ? "red" : "green"}
            />
            <KpiCard
              icon={TrendingUp}
              label="Early Arrivals"
              value={`${formatCount(earlyArrivalsCount)} (${formatPercent(earlyArrivalsPercentage)})`}
              helper="Positive punctuality indicator"
              tone="green"
            />
          </div>
        </section>

        <section className="tcdb-insight-grid">
          <InsightCard
            icon={CheckCircle2}
            title="Attendance Health"
            value={`${formatPercent(attendanceHealthValue)}`}
            note="Employees not falling under required hours"
            progress={attendanceHealthValue}
            tone={attendanceHealthTone}
          />

          <InsightCard
            icon={Clock}
            title="Punctuality"
            value={`${formatPercent(earlyPercentage + onTimePercentage)}`}
            note={`${formatPercent(latePercentage)} late log time`}
            progress={earlyPercentage + onTimePercentage}
            tone={latePercentage > 20 ? "amber" : "green"}
          />

          <InsightCard
            icon={Activity}
            title="Over Hours"
            value={`${formatCount(overHoursCount)} (${formatPercent(overHoursPercentage)})`}
            note="Monitor overtime and workload balance"
            progress={overHoursPercentage}
            tone={overHoursPercentage > 25 ? "amber" : "blue"}
          />

          <InsightCard
            icon={UserX}
            title="Exceptions"
            value={formatCount(underHoursEmployees.length)}
            note="Employees listed under required hours"
            progress={underHoursPercentage}
            tone={underHoursEmployees.length > 0 ? "red" : "green"}
          />
        </section>

        <section className="tcdb-focus-grid">
          <div className="tcdb-panel">
            <SectionHeader
              icon={TimerReset}
              title="Work-Hour Focus"
              subtitle="Important attendance signals for business review"
            />

            <div className="tcdb-focus-list">
              <div className="tcdb-focus-row">
                <span>Total Employees</span>
                <strong>{formatCount(totalEmployees)}</strong>
              </div>
              <div className="tcdb-focus-row">
                <span>Under Required Hours</span>
                <strong className={underHoursCount > 0 ? "tcdb-red" : "tcdb-green"}>
                  {formatCount(underHoursCount)}
                </strong>
              </div>
              <div className="tcdb-focus-row">
                <span>Over Required Hours</span>
                <strong>{formatCount(overHoursCount)}</strong>
              </div>
              <div className="tcdb-focus-row">
                <span>Early Arrivals</span>
                <strong className="tcdb-green">{formatCount(earlyArrivalsCount)}</strong>
              </div>
            </div>
          </div>

          <div className="tcdb-panel">
            <SectionHeader
              icon={PieChartIcon}
              title="Log Time Distribution"
              subtitle="Early, on-time and late percentage split"
            />

            <div className="tcdb-chart-box">
              <PieChart
                noMargin
                labels={pieData.labels}
                datasets={pieData.datasets}
                title=""
                height="100%"
                noShadow
                noBorder
              />
            </div>
          </div>
        </section>

        <section className="tcdb-panel">
          <SectionHeader
            icon={BarChart3}
            title="Weekly Attendance Pattern"
            subtitle="Early/on-time vs late employee logs for each day"
          />

          <div className="tcdb-chart-box tcdb-chart-box-wide">
            <BarChart
              labels={weeklyData.labels}
              datasets={weeklyData.datasets}
              title=""
              height="100%"
              noShadow
              noBorder
              noMargin
            />
          </div>
        </section>

        <section className="tcdb-panel">
          <SectionHeader
            icon={AlertTriangle}
            title="Employees Under Required Hours"
            subtitle="Compact exception list for follow-up by HR or management"
          />

          {visibleUnderHoursEmployees.length > 0 ? (
            <>
              <div className="tcdb-exception-table-wrap">
                <table className="tcdb-exception-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Expected</th>
                      <th>Actual</th>
                      <th>Gap</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleUnderHoursEmployees.map((employee, index) => {
                      const expectedHours = safeNumber(employee.expected_hours);
                      const actualHours = safeNumber(employee.actual_hours);
                      const gap = Math.max(expectedHours - actualHours, 0);

                      return (
                        <tr key={index}>
                          <td>
                            <strong className="tcdb-employee-name">{employee.employee_name || "Employee"}</strong>
                          </td>
                          <td>{expectedHours}h</td>
                          <td>
                            <span className="tcdb-pill tcdb-pill-red">{actualHours}h</span>
                          </td>
                          <td>
                            <span className="tcdb-gap">{gap.toFixed(gap % 1 === 0 ? 0 : 1)}h short</span>
                          </td>
                          <td>{employee.date || "N/A"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {remainingUnderHoursCount > 0 && (
                <div className="tcdb-more-note">
                  Showing top {visibleUnderHoursEmployees.length} employees. {remainingUnderHoursCount} more under-hour records available in source data.
                </div>
              )}
            </>
          ) : (
            <EmptyState message="No employees under required hours. Attendance health looks good." />
          )}
        </section>
      </div>
    </div>
  );
}

const dashboardStyles = `
  .tcdb-page {
    min-height: 100vh;
    padding: 10px 12px 28px;
    background:
      radial-gradient(circle at top left, rgba(37, 99, 235, 0.08), transparent 28%),
      radial-gradient(circle at top right, rgba(16, 185, 129, 0.07), transparent 28%),
      linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    color: #0f172a;
  }

  .tcdb-shell {
    max-width: 1450px;
    margin: 0 auto;
  }

  .tcdb-hero {
    position: relative;
    overflow: hidden;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(440px, 0.85fr);
    gap: 18px;
    align-items: center;
    padding: 22px;
    margin-bottom: 14px;
    border-radius: 28px;
    color: #ffffff;
    background: linear-gradient(135deg, #020617 0%, #111827 56%, #075985 100%);
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
  }

  .tcdb-hero::after {
    content: "";
    position: absolute;
    width: 320px;
    height: 320px;
    right: -120px;
    top: -160px;
    border-radius: 999px;
    background: rgba(56, 189, 248, 0.16);
  }

  .tcdb-hero > * {
    position: relative;
    z-index: 1;
  }

  .tcdb-kicker {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 11px;
    border-radius: 999px;
    margin-bottom: 10px;
    font-size: 11px;
    font-weight: 950;
    color: #e0f2fe;
    background: rgba(255, 255, 255, 0.13);
    border: 1px solid rgba(255, 255, 255, 0.22);
  }

  .tcdb-kicker-dot {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #22c55e;
    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.16);
  }

  .tcdb-hero h1 {
    margin: 0;
    font-size: 31px;
    line-height: 1.08;
    font-weight: 950;
    letter-spacing: -0.045em;
  }

  .tcdb-hero p {
    max-width: 700px;
    margin: 8px 0 0;
    color: rgba(255, 255, 255, 0.80);
    font-size: 13px;
    font-weight: 700;
    line-height: 1.6;
  }

  .tcdb-date-pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin-top: 13px;
    padding: 7px 11px;
    border-radius: 999px;
    color: #bfdbfe;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.18);
    font-size: 11px;
    font-weight: 900;
  }

  .tcdb-hero-kpis {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .tcdb-kpi-card,
  .tcdb-insight-card,
  .tcdb-panel {
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid #dbe3ec;
    box-shadow: 0 16px 42px rgba(15, 23, 42, 0.075);
  }

  .tcdb-kpi-card {
    display: flex;
    align-items: center;
    gap: 13px;
    min-height: 82px;
    padding: 13px;
    border-radius: 20px;
  }

  .tcdb-kpi-icon,
  .tcdb-insight-icon,
  .tcdb-section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--tcdb-accent);
    background: var(--tcdb-soft);
    border: 1px solid var(--tcdb-border);
    flex-shrink: 0;
  }

  .tcdb-kpi-icon {
    width: 44px;
    height: 44px;
    border-radius: 15px;
  }

  .tcdb-kpi-label {
    color: #475569;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .tcdb-kpi-value {
    margin-top: 2px;
    color: #0f172a;
    font-size: 21px;
    font-weight: 950;
    letter-spacing: -0.035em;
  }

  .tcdb-kpi-helper {
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
  }

  .tcdb-insight-grid,
  .tcdb-focus-grid {
    display: grid;
    gap: 14px;
    margin-bottom: 14px;
  }

  .tcdb-insight-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .tcdb-focus-grid {
    grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
  }

  .tcdb-insight-card {
    border-radius: 22px;
    padding: 15px;
  }

  .tcdb-insight-top {
    display: flex;
    gap: 11px;
    align-items: flex-start;
  }

  .tcdb-insight-icon {
    width: 42px;
    height: 42px;
    border-radius: 15px;
  }

  .tcdb-insight-card h3 {
    margin: 0;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
  }

  .tcdb-insight-card p {
    margin: 4px 0 0;
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
    line-height: 1.45;
  }

  .tcdb-insight-value {
    margin-top: 14px;
    color: #0f172a;
    font-size: 26px;
    font-weight: 950;
    letter-spacing: -0.04em;
  }

  .tcdb-panel {
    border-radius: 24px;
    padding: 16px;
    margin-bottom: 14px;
  }

  .tcdb-section-header {
    display: flex;
    align-items: center;
    gap: 11px;
    margin-bottom: 14px;
  }

  .tcdb-section-icon {
    width: 40px;
    height: 40px;
    border-radius: 15px;
    --tcdb-accent: #0f172a;
    --tcdb-soft: #f8fafc;
    --tcdb-border: #dbe3ec;
  }

  .tcdb-section-header h2 {
    margin: 0;
    color: #0f172a;
    font-size: 17px;
    font-weight: 950;
    letter-spacing: -0.025em;
  }

  .tcdb-section-header p {
    margin: 3px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .tcdb-focus-list {
    display: grid;
    gap: 10px;
  }

  .tcdb-focus-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border-radius: 16px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
  }

  .tcdb-focus-row span {
    color: #475569;
    font-size: 12px;
    font-weight: 850;
  }

  .tcdb-focus-row strong {
    color: #0f172a;
    font-size: 15px;
    font-weight: 950;
  }

  .tcdb-chart-box {
    height: 285px;
    min-height: 285px;
  }

  .tcdb-chart-box-wide {
    height: 310px;
  }

  .tcdb-progress {
    width: 100%;
    height: 8px;
    margin-top: 10px;
    border-radius: 999px;
    background: #e2e8f0;
    overflow: hidden;
  }

  .tcdb-progress span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: var(--tcdb-accent);
  }

  .tcdb-exception-table-wrap {
    width: 100%;
    overflow-x: auto;
  }

  .tcdb-exception-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 7px;
    min-width: 720px;
  }

  .tcdb-exception-table th {
    padding: 10px 12px;
    color: #64748b;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    font-size: 11px;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }

  .tcdb-exception-table th:first-child {
    border-left: 1px solid #e2e8f0;
    border-radius: 13px 0 0 13px;
  }

  .tcdb-exception-table th:last-child {
    border-right: 1px solid #e2e8f0;
    border-radius: 0 13px 13px 0;
  }

  .tcdb-exception-table td {
    padding: 11px 12px;
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;
    color: #334155;
    font-size: 13px;
    font-weight: 750;
    vertical-align: middle;
  }

  .tcdb-exception-table td:first-child {
    border-left: 1px solid #e2e8f0;
    border-radius: 13px 0 0 13px;
  }

  .tcdb-exception-table td:last-child {
    border-right: 1px solid #e2e8f0;
    border-radius: 0 13px 13px 0;
  }

  .tcdb-employee-name {
    color: #0f172a;
    font-weight: 950;
  }

  .tcdb-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 48px;
    padding: 5px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 950;
    white-space: nowrap;
  }

  .tcdb-pill-red {
    background: #fef2f2;
    color: #b91c1c;
    border: 1px solid #fecaca;
  }

  .tcdb-gap {
    color: #b91c1c;
    font-weight: 950;
  }

  .tcdb-more-note {
    margin-top: 11px;
    padding: 10px 12px;
    border-radius: 14px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    color: #92400e;
    font-size: 12px;
    font-weight: 850;
  }

  .tcdb-empty-state {
    min-height: 190px;
    border: 1px dashed #cbd5e1;
    border-radius: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #047857;
    font-size: 13px;
    font-weight: 850;
    background: #f0fdf4;
  }

  .tcdb-empty-page {
    min-height: 430px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    color: #0f172a;
  }

  .tcdb-empty-page-icon {
    width: 72px;
    height: 72px;
    border-radius: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #eff6ff;
    color: #2563eb;
    border: 1px solid #bfdbfe;
    margin-bottom: 16px;
  }

  .tcdb-empty-page h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 950;
  }

  .tcdb-empty-page p {
    max-width: 480px;
    margin: 8px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .tcdb-green {
    color: #059669 !important;
  }

  .tcdb-red {
    color: #dc2626 !important;
  }

  .tcdb-tone-blue {
    --tcdb-accent: #2563eb;
    --tcdb-soft: #eff6ff;
    --tcdb-border: #bfdbfe;
  }

  .tcdb-tone-green {
    --tcdb-accent: #059669;
    --tcdb-soft: #ecfdf5;
    --tcdb-border: #a7f3d0;
  }

  .tcdb-tone-red {
    --tcdb-accent: #dc2626;
    --tcdb-soft: #fef2f2;
    --tcdb-border: #fecaca;
  }

  .tcdb-tone-amber {
    --tcdb-accent: #d97706;
    --tcdb-soft: #fffbeb;
    --tcdb-border: #fde68a;
  }

  .tcdb-loading {
    min-height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    color: #0f172a;
  }

  .tcdb-loading h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 950;
  }

  .tcdb-loading p {
    margin: 5px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .tcdb-spinner {
    width: 54px;
    height: 54px;
    border-radius: 999px;
    border: 5px solid #dbeafe;
    border-top-color: #2563eb;
    animation: tcdb-spin 0.8s linear infinite;
  }

  @keyframes tcdb-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 1200px) {
    .tcdb-hero {
      grid-template-columns: 1fr;
    }

    .tcdb-insight-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .tcdb-focus-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .tcdb-page {
      padding: 8px 8px 22px;
    }

    .tcdb-hero {
      border-radius: 22px;
      padding: 18px;
    }

    .tcdb-hero h1 {
      font-size: 25px;
    }

    .tcdb-insight-grid {
      grid-template-columns: 1fr;
    }

    .tcdb-panel,
    .tcdb-insight-card {
      border-radius: 20px;
      padding: 14px;
    }

    .tcdb-chart-box,
    .tcdb-chart-box-wide {
      height: 280px;
      min-height: 280px;
    }
  }
`;
